import * as React from 'react';
import {useEffect, useState} from 'react';
import {cloneDeep} from 'lodash';
import InboundContext from '../contexts/WarehouseInboundContext';
import {Inbound, InboundPackagingMap, InboundState, UNKNOWN_TYPE} from '../shared/InboundInterfaces';
import {ApiResponse, ApiResponseV2, Company, Document, Reservation} from '../../shared/CommonInterfaces';
import {ContainerDeliveryInProgressStates, ContainerDeliveryState} from '../../dropoffs/shared/DropoffInterfaces';
import InboundDetailsHeaderPanel from '../shared/InboundDetailsHeaderPanel';
import InboundDocuments from '../shared/InboundDocuments';
import {InboundSummary} from '../shared/InboundSummary';
import AlteredInboundConfirmation from './AlteredInboundConfirmation';
import InboundArrivalStep from './InboundArrivalStep';
import ConfirmInbound from './ConfirmInbound';
import InboundTypeSelectionStep from './InboundTypeSelectionStep';

interface Props {
  inboundId: number;
  warehouseId: number;
  enableInboundPackaging: boolean;
  enableLpn: boolean;
  lpnReceiveOnly: boolean;
  showEditLpnForAsn: boolean;
  allowLpnTransferInAllInboundStates: boolean;
  receivingListPath: string;
}

export type InboundApiResponse = ApiResponse<any> | ApiResponseV2 | null;

const InboundDeliveryDetails: React.FunctionComponent<Props> = (props) => {
  const {inboundService, containersService, warehouseService, packingListService, documentsService} = React.useContext(
    InboundContext
  );

  const [errors, setErrors] = React.useState<string[]>([]);
  const [inbound, setInbound] = React.useState<Inbound>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [containerLoaded, setContainerLoaded] = React.useState<boolean>(false);
  const [inputTextMap, setInputTextMap] = useState<Map<string, string>>(new Map<string, string>());

  const [shipperCompany, setShipperCompany] = React.useState<Company>(null);
  const [reservation, setReservation] = React.useState<Reservation>(null);
  const [isBolUploaded, setIsBolUploaded] = useState<boolean>(false);

  useEffect(() => {
    loadInboundDetails();
  }, []);

  const handleApiError = (err: string[], response: InboundApiResponse) => {
    if (response?.errors?.length > 0) {
      err[0] += ':';
      response.errors.map((error: {detail: string}) => err.push(error.detail));
    }
    setErrors(err);
  };

  const onUpdateInstructions = (newInstructions: string) => {
    setInbound((currentInbound) => ({
      ...currentInbound,
      instructions: newInstructions
    }));
  };

  const handleInputTextMap = (eventTargetValue: string, propertyKey: string) => {
    const updatedInputTextMap: Map<string, string> = new Map<string, string>(inputTextMap);
    updatedInputTextMap.set(propertyKey, eventTargetValue);
    setInputTextMap(updatedInputTextMap);
  };

  const loadInboundDetails = async () => {
    const response = await inboundService.getInboundById(props.inboundId);
    if (response?.errors?.length > 0 || !response?.data) {
      setContainerLoaded(false);
      handleApiError([`Failed to load inbound detail #${props.inboundId}`], response);
      return;
    } else {
      setErrors([]);
      const inboundResponse = response.data as Inbound;

      const inboundReservation: Reservation = {
        id: inboundResponse.reservation.id
      };
      const inboundShipperCompany: Company = {
        id: inboundResponse.reservation.shipperId,
        name: inboundResponse.reservation.shipperName
      };
      setInbound(inboundResponse);
      setReservation(inboundReservation);
      setShipperCompany(inboundShipperCompany);
      setContainerLoaded(true);
    }
  };

  const handleSavedDoc = (savedDoc: Document) => {
    const hasBillOfLanding = savedDoc.documentType === 'Bill Of Lading' && savedDoc.company.id !== shipperCompany.id;

    const updatedInbound = cloneDeep(inbound);
    updatedInbound.hasBillOfLading = hasBillOfLanding;
    setInbound(updatedInbound);
  };

  const handleIsBolUploaded = (isUploaded: boolean) => {
    setIsBolUploaded(isUploaded);
  };

  const isActualInboundPackagingSet = () => {
    return (
      !props.enableInboundPackaging ||
      (props.enableInboundPackaging &&
        inbound.actualInboundPackaging !== null &&
        InboundPackagingMap.get(inbound.actualInboundPackaging) !== UNKNOWN_TYPE)
    );
  };

  return (
    <div className="content">
      {inbound && reservation && (
        <div className="new-inbound-header-panel">
          <div id="inbound-details-page">
            <InboundDetailsHeaderPanel
              inbound={inbound}
              containersService={containersService}
              onUpdateInstructions={onUpdateInstructions}
              errors={errors}
              reservation={reservation}
              enableInboundPackaging={props.enableInboundPackaging}
              onInputTextMap={handleInputTextMap}
              inputTextMap={inputTextMap}
              isEditing={isEditing}
              isShipper={false}
              isEditMode={false}
              handleRefreshPage={() => window.location.reload()}
            />

            <div id="inbound-additional-details" className="warehouse-info-and-docs">
              <InboundDocuments
                isShipper={false}
                inbound={inbound}
                reservation={reservation}
                onDocumentSaved={handleSavedDoc}
                handleIsBolUploaded={handleIsBolUploaded}
                isContainerDeliveryArrived={inbound.actualArrivalTime !== null}
                isEditMode={false}
                handleRefreshPage={() => window.location.reload()}
                documentsService={documentsService}
              />
            </div>
          </div>
        </div>
      )}

      <div className="container-fluid" id="inbound-detail">
        {inbound?.state === InboundState.New.toString() && (
          <ConfirmInbound
            inbound={inbound}
            containersService={containersService}
            instructions={inbound.instructions}
            handleApiError={handleApiError}
            reloadContainer={loadInboundDetails}
          />
        )}
        {inbound?.state === InboundState.Altered.toString() && (
          <AlteredInboundConfirmation
            containersService={containersService}
            inbound={inbound}
            handleApiError={handleApiError}
            reloadContainer={loadInboundDetails}
          />
        )}
        {ContainerDeliveryInProgressStates.includes(inbound?.state as ContainerDeliveryState) && (
          <>
            {!inbound.actualArrivalTime && (
              <InboundArrivalStep
                inboundService={inboundService}
                inbound={inbound}
                handleApiError={handleApiError}
                reloadInbound={loadInboundDetails}
              />
            )}

            {!isActualInboundPackagingSet() && inbound.actualArrivalTime && (
              <InboundTypeSelectionStep
                inboundService={inboundService}
                inbound={inbound}
                handleApiError={handleApiError}
                reloadInbound={loadInboundDetails}
                lpnReceiveOnly={props.lpnReceiveOnly}
              />
            )}
          </>
        )}

        {inbound && reservation && (
          <InboundSummary
            containersService={containersService}
            inboundService={inboundService}
            warehouseService={warehouseService}
            packingListService={packingListService}
            reservation={reservation}
            lpnReceiveOnly={props.lpnReceiveOnly}
            inbound={inbound}
            inboundState={inbound.state as InboundState}
            showEditLpnForAsn={props.showEditLpnForAsn}
            warehouseProps={{
              isBolUploaded,
              isInboundPackagingSet: isActualInboundPackagingSet(),
              handleInboundCompletionErrors: handleApiError,
              reloadContainer: loadInboundDetails,
              warehouseId: props.warehouseId
            }}
          />
        )}
      </div>
    </div>
  );
};

export default InboundDeliveryDetails;
